import React, { useState, useEffect, useRef } from 'react';
import { FiStar, FiSend, FiCamera, FiX, FiThumbsUp, FiCheck, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { toast } from 'react-toastify';
import reviewService from '../../services/review.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const StarRating = ({ rating, onRate, interactive = false, size = 18 }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="d-flex align-items-center gap-1">
      {Array.from({ length: 5 }, (_, i) => {
        const v = i + 1;
        const filled = v <= (interactive ? (hover || rating) : rating);
        return (
          <FiStar key={i} size={size} fill={filled ? '#EAB308' : 'none'} stroke="#EAB308"
            style={interactive ? { cursor: 'pointer' } : {}}
            onClick={() => interactive && onRate?.(v)}
            onMouseEnter={() => interactive && setHover(v)}
            onMouseLeave={() => interactive && setHover(0)} />
        );
      })}
    </div>
  );
};

// ── LIGHTBOX ──
const ImageLightbox = ({ images, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  if (!images?.length) return null;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.9)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <button onClick={onClose} style={{
        position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none',
        color: '#FFF', cursor: 'pointer', zIndex: 10001,
      }}><FiX size={28} /></button>

      {images.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); setIdx(i => (i - 1 + images.length) % images.length); }}
            style={{ position: 'absolute', left: '16px', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#FFF', borderRadius: '50%', width: '44px', height: '44px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FiChevronLeft size={24} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setIdx(i => (i + 1) % images.length); }}
            style={{ position: 'absolute', right: '16px', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#FFF', borderRadius: '50%', width: '44px', height: '44px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FiChevronRight size={24} />
          </button>
        </>
      )}

      <img src={images[idx]?.startsWith('http') ? images[idx] : `${API_URL}${images[idx]}`} alt=""
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '8px' }} />

      <div style={{ position: 'absolute', bottom: '20px', color: '#FFF', fontSize: '14px', fontWeight: 600 }}>
        {idx + 1} / {images.length}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
const ReviewSection = ({ productId, isAuthenticated }) => {
  const [reviews, setReviews] = useState([]);
  const [ratingBreakdown, setRatingBreakdown] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ rating: 0, title: '', comment: '' });
  const [selectedFiles, setSelectedFiles] = useState([]); // File objects
  const [previews, setPreviews] = useState([]); // data URLs
  const [lightbox, setLightbox] = useState(null); // { images, index }
  const fileInput = useRef(null);

  const fetchReviews = async (page = 1) => {
    try {
      setLoading(true);
      const { data } = await reviewService.getByProduct(productId, { page, limit: 5 });
      setReviews(data.data);
      setRatingBreakdown(data.ratingBreakdown || []);
      setPagination(data.pagination);
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchReviews(); }, [productId]);

  // ── Image handling ──
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    addFiles(files);
  };

  const addFiles = (files) => {
    const remaining = 5 - selectedFiles.length;
    if (remaining <= 0) { toast.info('Maximum 5 images allowed'); return; }
    const newFiles = files.slice(0, remaining).filter(f => f.type.startsWith('image/') && f.size <= 5 * 1024 * 1024);
    if (newFiles.length < files.length) toast.info('Some files were skipped (must be images under 5MB)');

    setSelectedFiles(prev => [...prev, ...newFiles]);
    newFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => setPreviews(prev => [...prev, e.target.result]);
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (i) => {
    setSelectedFiles(prev => prev.filter((_, j) => j !== i));
    setPreviews(prev => prev.filter((_, j) => j !== i));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.rating === 0) { toast.warning('Please select a rating'); return; }
    setSubmitting(true);
    try {
      await reviewService.create(productId, { ...formData, images: selectedFiles });
      toast.success('Review submitted!');
      setFormData({ rating: 0, title: '', comment: '' });
      setSelectedFiles([]);
      setPreviews([]);
      setShowForm(false);
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit review');
    }
    setSubmitting(false);
  };

  // ── Helpful ──
  const handleHelpful = async (reviewId) => {
    try {
      await reviewService.markHelpful(productId, reviewId);
      setReviews(prev => prev.map(r => r._id === reviewId ? { ...r, helpfulCount: (r.helpfulCount || 0) + 1 } : r));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  // ── Rating bars ──
  const totalReviews = ratingBreakdown.reduce((sum, r) => sum + r.count, 0);
  const avgRating = totalReviews > 0 ? ratingBreakdown.reduce((sum, r) => sum + r._id * r.count, 0) / totalReviews : 0;
  const ratingBars = [5, 4, 3, 2, 1].map(star => {
    const found = ratingBreakdown.find(r => r._id === star);
    const count = found ? found.count : 0;
    return { star, count, percent: totalReviews > 0 ? (count / totalReviews) * 100 : 0 };
  });

  return (
    <div>
      {/* Rating Summary */}
      {totalReviews > 0 && (
        <div className="row g-4 mb-4">
          <div className="col-md-4 text-center">
            <div style={{ fontSize: '42px', fontWeight: 800, color: '#B91C1C' }}>{avgRating.toFixed(1)}</div>
            <StarRating rating={Math.round(avgRating)} size={20} />
            <p className="text-muted small mt-1">{totalReviews} review{totalReviews !== 1 ? 's' : ''}</p>
          </div>
          <div className="col-md-8">
            {ratingBars.map(({ star, count, percent }) => (
              <div key={star} className="d-flex align-items-center gap-2 mb-1">
                <span className="small" style={{ width: '50px' }}>{star} stars</span>
                <div style={{ flex: 1, height: '10px', background: '#F3F4F6', borderRadius: '5px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${percent}%`, height: '100%', borderRadius: '5px',
                    background: star >= 4 ? '#10B981' : star === 3 ? '#F59E0B' : '#EF4444',
                    transition: 'width 0.4s',
                  }} />
                </div>
                <span className="small text-muted" style={{ width: '30px' }}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Write Review Button */}
      {isAuthenticated && !showForm && (
        <button className="btn btn-fd-outline mb-4" onClick={() => setShowForm(true)}>
          ✍️ Write a Review
        </button>
      )}

      {/* Review Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-4 p-4" style={{ background: '#FAFAFA', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
          <h6 style={{ fontWeight: 700, marginBottom: '16px' }}>Write Your Review</h6>

          <div className="mb-3">
            <label className="form-label" style={{ fontSize: '13px', fontWeight: 600 }}>Rating *</label>
            <StarRating rating={formData.rating} onRate={r => setFormData({ ...formData, rating: r })} interactive size={28} />
          </div>

          <div className="mb-3">
            <label className="form-label" style={{ fontSize: '13px', fontWeight: 600 }}>Title</label>
            <input className="form-control" placeholder="Brief summary" value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })} maxLength={200} />
          </div>

          <div className="mb-3">
            <label className="form-label" style={{ fontSize: '13px', fontWeight: 600 }}>Comment</label>
            <textarea className="form-control" rows={3} placeholder="Share your experience..."
              value={formData.comment} onChange={e => setFormData({ ...formData, comment: e.target.value })} maxLength={2000} />
          </div>

          {/* Image Upload */}
          <div className="mb-3">
            <label className="form-label" style={{ fontSize: '13px', fontWeight: 600 }}>
              <FiCamera size={14} className="me-1" /> Photos (optional, max 5)
            </label>

            <div
              onDragOver={e => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInput.current?.click()}
              style={{
                border: '2px dashed #D1D5DB', borderRadius: '10px', padding: '20px',
                textAlign: 'center', cursor: 'pointer', background: '#FFF',
                transition: 'border-color 0.2s',
              }}
            >
              <FiCamera size={24} color="#9CA3AF" />
              <p style={{ fontSize: '13px', color: '#6B7280', margin: '8px 0 0' }}>
                Click or drag & drop images here
              </p>
              <input ref={fileInput} type="file" accept="image/*" multiple onChange={handleFileSelect}
                style={{ display: 'none' }} />
            </div>

            {/* Previews */}
            {previews.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {previews.map((src, i) => (
                  <div key={i} style={{ position: 'relative', width: '72px', height: '72px' }}>
                    <img src={src} alt="" style={{
                      width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px',
                      border: '2px solid #E5E7EB',
                    }} />
                    <button onClick={(e) => { e.preventDefault(); removeFile(i); }}
                      style={{
                        position: 'absolute', top: '-6px', right: '-6px', width: '20px', height: '20px',
                        borderRadius: '50%', background: '#EF4444', color: '#FFF', border: 'none',
                        fontSize: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                      <FiX size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-fd-primary" disabled={submitting}>
              <FiSend className="me-1" /> {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
            <button type="button" className="btn btn-outline-secondary"
              onClick={() => { setShowForm(false); setSelectedFiles([]); setPreviews([]); }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Review List */}
      {loading ? (
        <p className="text-muted">Loading reviews...</p>
      ) : reviews.length > 0 ? (
        <div>
          {reviews.map(review => (
            <div key={review._id} className="py-3" style={{ borderBottom: '1px solid #F3F4F6' }}>
              <div className="d-flex align-items-center gap-2 mb-1">
                <StarRating rating={review.rating} size={14} />
                {review.title && <strong style={{ fontSize: '14px' }}>{review.title}</strong>}
              </div>

              {review.comment && (
                <p style={{ fontSize: '14px', lineHeight: 1.6, margin: '6px 0' }}>{review.comment}</p>
              )}

              {/* Review Images */}
              {review.images?.length > 0 && (
                <div className="d-flex flex-wrap gap-2 my-2">
                  {review.images.map((img, i) => (
                    <img key={i}
                      src={img.startsWith('http') ? img : `${API_URL}${img}`}
                      alt={`Review photo ${i + 1}`}
                      onClick={() => setLightbox({ images: review.images, index: i })}
                      style={{
                        width: '68px', height: '68px', objectFit: 'cover', borderRadius: '8px',
                        cursor: 'pointer', border: '2px solid #E5E7EB',
                        transition: 'transform 0.2s, border-color 0.2s',
                      }}
                      onMouseEnter={e => { e.target.style.transform = 'scale(1.05)'; e.target.style.borderColor = '#B91C1C'; }}
                      onMouseLeave={e => { e.target.style.transform = 'scale(1)'; e.target.style.borderColor = '#E5E7EB'; }}
                    />
                  ))}
                </div>
              )}

              {/* Meta */}
              <div className="d-flex align-items-center gap-3 mt-1">
                <small className="text-muted">
                  By <strong>{review.userName}</strong> on {new Date(review.createdAt).toLocaleDateString()}
                </small>
                {review.isVerifiedPurchase && (
                  <span style={{
                    fontSize: '11px', fontWeight: 700, color: '#059669',
                    display: 'flex', alignItems: 'center', gap: '3px',
                  }}>
                    <FiCheck size={12} /> Verified Purchase
                  </span>
                )}
                {isAuthenticated && (
                  <button onClick={() => handleHelpful(review._id)}
                    style={{
                      background: 'none', border: '1px solid #E5E7EB', borderRadius: '14px',
                      padding: '2px 10px', fontSize: '12px', cursor: 'pointer', color: '#6B7280',
                      display: 'flex', alignItems: 'center', gap: '4px',
                    }}>
                    <FiThumbsUp size={12} /> Helpful {review.helpfulCount > 0 ? `(${review.helpfulCount})` : ''}
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <nav className="mt-3">
              <ul className="pagination pagination-sm">
                {Array.from({ length: pagination.pages }, (_, i) => (
                  <li key={i + 1} className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => fetchReviews(i + 1)}>{i + 1}</button>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </div>
      ) : (
        <p className="text-muted">No reviews yet. Be the first to review this product!</p>
      )}

      {/* Lightbox */}
      {lightbox && (
        <ImageLightbox images={lightbox.images} startIndex={lightbox.index}
          onClose={() => setLightbox(null)} />
      )}
    </div>
  );
};

export default ReviewSection;
